import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
    MutableRefObject,
    PropsWithChildren,
    ReactElement,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { PaloConfirmationDialog, PaloFieldDirtyProvider } from '@synergy/components-palo';
import { isEqualWith } from 'lodash';
import { useBlocker } from 'react-router-dom';
import { useFormikContext } from 'formik';

import { compareValues, dirtyFieldNames } from './palo-dirty-fields';
import { isCreationStep } from './palo-creation-step';

type LeaveHandler = (() => void) | undefined;
type DirtyMarks = Record<string, boolean>;

/** Which fields of a form differ from what the server has, by name. */
type DirtyFields = Record<string, string[]>;

/** Key of everything that is not a wizard step — one form at a time. */
const FORM_KEY = 'form';

interface PaloDirtyContextValue {
    marks: DirtyMarks;
    fields: DirtyFields;
    setDirty: (key: string, dirty: boolean) => void;
    /** Drops every mark. A document freshly loaded from the server is clean. */
    clearDirty: () => void;
    /** See usePaloSaveOnLeave. Held in a ref because it changes on every render. */
    leaveHandlerRef: MutableRefObject<LeaveHandler>;
    setDirtyFields: (key: string, names: string[] | undefined) => void;
}

const noLeaveHandler: MutableRefObject<LeaveHandler> = { current: undefined };

const PaloDirtyContext = createContext<PaloDirtyContextValue>({
    marks: {},
    fields: {},
    setDirty: () => undefined,
    clearDirty: () => undefined,
    leaveHandlerRef: noLeaveHandler,
    setDirtyFields: () => undefined,
});

const sameNames = (left: string[], right: string[]): boolean =>
    left.length === right.length && left.every((name, index) => name === right[index]);

/** Names the wizard step rendering inside it; undefined outside a wizard. */
const PaloDirtyStepContext = createContext<string | undefined>(undefined);

const hasAnyMark = (marks: DirtyMarks) => Object.values(marks).some(Boolean);

/**
 * Guards the way out of a mask with unsaved input — the second half of the
 * customer's wish, next to the tab marker.
 *
 * The router allows exactly one blocker; a second one is ignored with a console
 * warning. That is why this sits in the provider and why masks that want to save
 * on the way out register through usePaloSaveOnLeave instead of blocking
 * themselves.
 */
/** Whether a path is the mask at `base` or one of its tabs. */
const within = (base: string, pathname: string): boolean => pathname === base || pathname.startsWith(base + '/');

const PaloDirtyGuard = ({
    dirty,
    basePath,
    leaveHandlerRef,
}: {
    dirty: boolean;
    basePath?: string;
    leaveHandlerRef: PaloDirtyContextValue['leaveHandlerRef'];
}): ReactElement => {
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        // A navigation onto the open path changes nothing and must not raise the
        // dialog — re-clicking the open tab is the common case.
        if (currentLocation.pathname === nextLocation.pathname) {
            return false;
        }

        if (isCreationStep(currentLocation.pathname, nextLocation.pathname)) {
            return false;
        }

        // Masks that save on the way out never ask; saving is the better answer
        // to the same problem.
        if (leaveHandlerRef.current) {
            leaveHandlerRef.current();

            return false;
        }

        // Moving between the tabs of one mask parks the input rather than losing
        // it, so there is nothing to ask about. Since the marks now outlive the
        // tab that made them (#2159), without this the mask would ask on every
        // tab whose form does not register a handler of its own.
        if (
            undefined !== basePath &&
            within(basePath, currentLocation.pathname) &&
            within(basePath, nextLocation.pathname)
        ) {
            return false;
        }

        return dirty;
    });

    useEffect(() => {
        if (!dirty) {
            return undefined;
        }

        // Covers closing the tab and reloading, which the router never sees. The
        // wording is the browser's own; it ignores whatever we put here.
        const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

        window.addEventListener('beforeunload', warnBeforeUnload);

        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [dirty]);

    return (
        <PaloConfirmationDialog
            isVisible={blocker.state === 'blocked'}
            title={<FormattedMessage id="app.common.unsavedChanges.title" />}
            action={<FormattedMessage id="app.common.deleteChanges" />}
            cancelText={<FormattedMessage id="app.common.unsavedChanges.stay" />}
            onConfirm={() => blocker.proceed?.()}
            onCancel={() => blocker.reset?.()}
        >
            <FormattedMessage id="app.common.unsavedChanges.warning" />
        </PaloConfirmationDialog>
    );
};

/**
 * Tracks which parts of the open mask have unsaved input. A tab mask only ever
 * has one entry, because only one tab is mounted; a wizard keeps one per step,
 * because a step that is left still holds its input in the wizard's working
 * state and is still unsaved.
 */
export const PaloDirtyProvider = ({
    basePath,
    loadedDocument,
    children,
}: PropsWithChildren<{
    /**
     * The mask's own route, e.g. `/customers/1`. Given it, moving between the
     * mask's tabs does not raise the unsaved-changes dialog — only leaving the
     * mask does.
     */
    basePath?: string;
    /**
     * The document as the server last handed it over. When a different one
     * arrives, every mark goes — that is the one moment at which every part of
     * the mask, including the parts not mounted, is provably saved.
     */
    loadedDocument?: unknown;
}>): ReactElement => {
    const [marks, setMarks] = useState<DirtyMarks>({});
    const [fields, setFields] = useState<DirtyFields>({});
    const leaveHandlerRef = useRef<LeaveHandler>(undefined);

    const setDirty = useCallback((key: string, dirty: boolean) => {
        setMarks(previous => (!!previous[key] === dirty ? previous : { ...previous, [key]: dirty }));
    }, []);

    const clearDirty = useCallback(() => {
        setMarks(previous => (hasAnyMark(previous) ? {} : previous));
        // The field marks go with them; leaving them behind would mark fields
        // on a mask whose steps all report themselves as saved.
        setFields(previous => (0 === Object.keys(previous).length ? previous : {}));
    }, []);

    const setDirtyFields = useCallback((key: string, names: string[] | undefined) => {
        setFields(previous => {
            const held = previous[key];

            if (!names) {
                if (!held) {
                    return previous;
                }

                const next = { ...previous };
                delete next[key];

                return next;
            }

            // The common case by far: the same field is still the changed one,
            // so nothing here re-renders while someone keeps typing in it.
            if (held && sameNames(held, names)) {
                return previous;
            }

            return { ...previous, [key]: names };
        });
    }, []);

    /**
     * Whether one field holds unsaved input.
     *
     * Reads across every form on the mask rather than one: a wizard mounts one
     * step at a time, and the field asking is always in the mounted one. Field
     * names do not collide between steps of the same document.
     *
     * A name below a changed one counts as changed too — a mask that binds
     * `customerAccount.name` is looking at a part of `customerAccount` — and
     * so does one above it: a field holding a whole list changed with any
     * entry.
     */
    const fieldDirtyLookup = useCallback(
        (name: string): boolean =>
            Object.values(fields).some(names =>
                names.some(
                    changed => changed === name || name.startsWith(changed + '.') || changed.startsWith(name + '.')
                )
            ),
        [fields]
    );

    // Only when a *different* document arrives, never on mount: the marks are
    // set while the mask is up, and clearing on mount would wipe them the first
    // time anything re-rendered.
    const seen = useRef<{ document: unknown } | undefined>(undefined);

    useEffect(() => {
        if (seen.current && seen.current.document !== loadedDocument) {
            clearDirty();
        }

        seen.current = { document: loadedDocument };
    }, [loadedDocument, clearDirty]);

    const value = useMemo(
        () => ({ marks, fields, setDirty, clearDirty, leaveHandlerRef, setDirtyFields }),
        [marks, fields, setDirty, clearDirty, setDirtyFields]
    );

    return (
        <PaloDirtyContext.Provider value={value}>
            <PaloDirtyGuard dirty={hasAnyMark(marks)} basePath={basePath} leaveHandlerRef={leaveHandlerRef} />
            <PaloFieldDirtyProvider value={fieldDirtyLookup}>{children}</PaloFieldDirtyProvider>
        </PaloDirtyContext.Provider>
    );
};

export const usePaloDirty = (): boolean => hasAnyMark(useContext(PaloDirtyContext).marks);

/**
 * Whether one wizard step holds unsaved input.
 *
 * The step a reader is looking at is the one they need telling about least.
 * Asking per step is what lets the marker do its job: say that something is
 * waiting somewhere else.
 */
export const usePaloStepDirty = (index: number): boolean => !!useContext(PaloDirtyContext).marks[`step-${index}`];

/**
 * The same reading for any step, for a wizard that renders its steps in a loop
 * and cannot call a hook per step.
 */
export const usePaloStepDirtyLookup = (): ((index: number) => boolean) => {
    const { marks } = useContext(PaloDirtyContext);

    return useCallback((index: number) => !!marks[`step-${index}`], [marks]);
};

/**
 * Whether one named part of the mask holds unsaved input — a tab, usually.
 *
 * The tab someone is looking at is the one they need telling about least; the
 * point of the marker is to say that something is waiting on another one.
 */
export const usePaloScopeDirty = (name: string): boolean => !!useContext(PaloDirtyContext).marks[name];

/** Which fields of one named part differ from the server's copy, by name. */
export const usePaloScopeDirtyFields = (name: string): string[] => useContext(PaloDirtyContext).fields[name] ?? [];

/**
 * Submits the open sub-page when the mask is left, instead of asking. Replaces a
 * blocker of its own, which the router would drop — see PaloDirtyGuard.
 */
export const usePaloSaveOnLeave = (handler: LeaveHandler): void => {
    const { leaveHandlerRef } = useContext(PaloDirtyContext);

    useEffect(() => {
        leaveHandlerRef.current = handler;
    });
};

/**
 * Gives the part of the mask inside it a mark of its own.
 *
 * A part with a name keeps its mark when it is left. That is what lets the
 * marker do its job: a wizard step and a mask tab both park their input rather
 * than save it, so leaving one does not make it saved.
 *
 * Wiping the marks is not its business — that belongs to the provider, which
 * sees the whole mask. A scope that cleared on its own document wiped the other
 * scopes' marks too, and in a wizard whose steps each fetch their own part,
 * arriving at the second step took the first step's mark with it.
 */
/**
 * Reports unsaved input that lives outside any form, under a name of its own:
 * the figures in the totals bar of an invoice, for one. The mark goes when the
 * component does.
 */
export const usePaloDirtyMark = (name: string, dirty: boolean): void => {
    const { setDirty } = useContext(PaloDirtyContext);

    useEffect(() => {
        setDirty(name, dirty);

        return () => setDirty(name, false);
    }, [name, dirty, setDirty]);
};

export const PaloDirtyScope = ({ name, children }: PropsWithChildren<{ name: string }>): ReactElement => (
    <PaloDirtyStepContext.Provider value={name}>{children}</PaloDirtyStepContext.Provider>
);

/** The wizard's flavour of PaloDirtyScope; the stepper reads `step-<index>`. */
export const PaloDirtyStepScope = ({ index, children }: PropsWithChildren<{ index: number }>): ReactElement => (
    <PaloDirtyScope name={`step-${index}`}>{children}</PaloDirtyScope>
);

/**
 * Reports its Formik's dirty state upward. Belongs inside the sub-page's
 * Formik — `useFormikSubPage` only reports on submit, so without this the
 * marker would appear after a save attempt instead of while typing.
 */
export const PaloDirtyReporter = (): null => {
    const { setDirty, setDirtyFields } = useContext(PaloDirtyContext);
    const stepKey = useContext(PaloDirtyStepContext);
    const { dirty, values, initialValues } = useFormikContext();
    const key = stepKey ?? FORM_KEY;

    useEffect(() => {
        setDirty(key, dirty);

        // A wizard step keeps its mark when it is left: its input is parked in
        // the wizard's working state, not on the server. A sub-page drops it,
        // because leaving one either saves or discards.
        return stepKey ? undefined : () => setDirty(key, false);
    }, [dirty, key, stepKey, setDirty]);

    // Formik's own initial values are the baseline here, which is the same
    // thing `dirty` above is measured against — so the field marks and the
    // form mark can never disagree.
    useEffect(() => {
        setDirtyFields(key, dirtyFieldNames(values, initialValues));
    }, [key, values, initialValues, setDirtyFields]);

    // Same rule as the mark above: a wizard step keeps what it reported when it
    // is left, a sub-page drops it. Clearing here unconditionally would take the
    // field marks away on a step change while the step's own mark stayed.
    useEffect(() => (stepKey ? undefined : () => setDirtyFields(key, undefined)), [key, stepKey, setDirtyFields]);

    return null;
};

interface PaloDirtyComparisonProps<Values> {
    /** The document as the server has it, in the shape this form compares in. */
    baseline: unknown;
    /**
     * Brings the form values into that same shape. A step that reshapes its
     * values when it hands them to the wizard — normalising a date to the start
     * of the day, post-processing markdown — must reshape here as well, or the
     * hand-over alone reads as a change. Apply it to the baseline too.
     */
    normalize?: (values: Values) => unknown;
}

/**
 * Reports unsaved input by comparing the form against a baseline of its own,
 * for wizard steps. Formik's own `dirty` cannot serve here: a step hands its
 * values to the wizard when it is left, and Formik would take that hand-over for
 * a save. The baseline is the document as the server has it, so the marker means
 * the same on every step: not yet saved.
 */
export function PaloDirtyComparison<Values>({ baseline, normalize }: PaloDirtyComparisonProps<Values>): null {
    const { setDirty, setDirtyFields } = useContext(PaloDirtyContext);
    const stepKey = useContext(PaloDirtyStepContext);
    const { values } = useFormikContext<Values>();
    const key = stepKey ?? FORM_KEY;
    const current = normalize ? normalize(values) : values;
    // No baseline yet means the document has not arrived — a form with nothing
    // to compare against holds no unsaved input, it holds nothing. Reporting
    // otherwise lit every tab of the article mask the moment it was opened,
    // because each of its tabs fetches its own part.
    const dirty = undefined !== baseline && !isEqualWith(current, baseline, compareValues);

    useEffect(() => {
        setDirty(key, dirty);

        // A wizard step keeps its mark when it is left: its input is parked in
        // the wizard's working state, not on the server. A sub-page drops it,
        // because leaving one either saves or discards.
        return stepKey ? undefined : () => setDirty(key, false);
    }, [dirty, key, stepKey, setDirty]);

    // The field marks compare the normalised values, the same ones the form
    // mark above is measured on. Comparing raw values here would mark a field
    // that only the hand-over to the wizard reshaped.
    //
    // Deliberately without a dependency list: `current` is a fresh object on
    // every render when normalize is set, so any list would either miss changes
    // or loop. setDirtyFields compares the names and does nothing when the same
    // fields are still the changed ones.
    useEffect(() => {
        setDirtyFields(key, undefined === baseline ? [] : dirtyFieldNames(current, baseline));
    });

    useEffect(() => (stepKey ? undefined : () => setDirtyFields(key, undefined)), [key, stepKey, setDirtyFields]);

    return null;
}

/**
 * The unsaved-changes marker for one named part of a mask — a tab, usually.
 *
 * Carries the names of the differing fields as a data attribute: invisible to a
 * reader, and the first thing to look at when the marker says something
 * surprising. Two of those surprises were traced that way (#2159), and both
 * turned out to be a transform that treated the form and the server's copy
 * differently.
 */
export const PaloDirtyDot = ({ scope }: { scope: string }): ReactElement | null => {
    const { marks, fields } = useContext(PaloDirtyContext);

    if (!marks[scope]) {
        return null;
    }

    return (
        <Box
            component="span"
            title="Ungespeicherte Änderungen"
            data-dirty-fields={(fields[scope] ?? []).join(' ')}
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'warning.main',
                display: 'inline-block',
            }}
        />
    );
};

/**
 * Marker for a wizard step. Inert outside a PaloDirtyProvider, so the legacy
 * stepper can render it unconditionally.
 */
/** `stepIndex` takes every step a tab shows, so a tab holding two forms marks either one. */
export const PaloStepDirtyDot = ({ stepIndex }: { stepIndex: number | number[] }): ReactElement | null => {
    const { marks } = useContext(PaloDirtyContext);
    const indexes = Array.isArray(stepIndex) ? stepIndex : [stepIndex];

    if (!indexes.some(index => marks[`step-${index}`])) {
        return null;
    }

    return (
        <Box
            component="span"
            title="Ungespeicherte Änderungen"
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'warning.main',
                display: 'inline-block',
                ml: '6px',
            }}
        />
    );
};
